import {
  AlmanacFileAncestor,
  AlmanacFileBreadcrumbsContext,
  AlmanacFileSummary,
  ArticleView,
  AssetType,
  UserRole,
} from 'types'
import Styles from './AlmanacFileBreadcrumbs.module.scss'
import cx from 'classnames'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { ButtonLink } from 'components/Button/ButtonLink'
import { ErrorBoundary } from '@sentry/react'
import { Button } from 'components/Button'
import { getFolderPath } from '../../utils/urlUtils'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { observer } from 'mobx-react-lite'
import { useRootStore } from '../../stores/RootStore'
import { forwardRef, PropsWithChildren, ReactNode, useEffect } from 'react'
import { FocusEvents } from '@react-types/shared'
import { HoverProps, PressProps } from '@react-aria/interactions'
import { AlmanacFileTitleWithTree } from '../../components/AlmanacFileTitle/AlmanacFileTitleWithTree'
import { useArticleView } from '../../providers/ArticleViewProvider/ArticleViewProvider'
import { CommonFolderIcon } from '../../screens/Finder/components/FileIcon/FileIcon'
import { generatePath, Link } from 'react-router-dom'
import { ButtonLinkProps, ButtonProps } from 'components/Button/Types'
import { ALL_FILES_PATH, SHARED_WITH_ME_DOCS_PATH } from 'paths'
import { Share } from 'almanac-ui/icons'
import { useAbility } from '@casl/react'
import { AbilityContext, CanView, canViewIn } from 'components/Can'
import { useWorkspace } from 'hooks/useWorkspace'
import { SubjectType } from 'utils/caslAbility'
import QueryString from 'qs'
import { useQueryParams } from 'hooks/useQueryParams'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { wordCountOfDoc } from 'utils/wordCountOfDoc'
import { FolderOpen } from 'blaze-ui/icons'
import dayjs from 'dayjs'

type BreadcrumbType = AlmanacFileAncestor
type Weight = 'light' | 'medium'

interface BreadcrumbLinkProps extends PressProps, HoverProps, FocusEvents {
  breadcrumb: BreadcrumbType
  withDefaultIcon?: boolean
  postAt?: string
}

interface BaseBreadcrumbProps extends PressProps, HoverProps, FocusEvents {
  to: ButtonLinkProps['to']
  icon: ReactNode
  title: string
}

const BaseBreadcrumb = forwardRef<HTMLAnchorElement, BaseBreadcrumbProps>(({ to, icon, title, ...rest }, fRef) => {
  return (
    <ButtonLink
      ref={fRef}
      {...rest}
      className={Styles.breadcrumbLink}
      variant="tertiary"
      size="md"
      to={to}
      elementType={Link}
      aria-label={title}
    >
      <span className={Styles.breadcrumbLinkTitle}>{title}</span>
    </ButtonLink>
  )
})

const BreadcrumbLink = observer(
  forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
    ({ breadcrumb, withDefaultIcon = false, postAt, ...rest }, fRef) => {
      const { title, emoji, seo_string } = breadcrumb
      const { focusView } = useQueryParams()
      const { leftSidebarStore } = useRootStore()
      const date = (postAt ? dayjs(postAt) : dayjs()).format('YYYY-MM-DD')

      return (
        <BaseBreadcrumb
          ref={fRef}
          to={{
            pathname: getFolderPath({ folderSlug: seo_string }),
            search: QueryString.stringify(
              {
                focusView,
                date,
              },
              { filter: (_, value) => value ?? undefined },
            ),
          }}
          title={title}
          icon={emoji || (withDefaultIcon && <CommonFolderIcon className={Styles.emoji} />) || null}
          onPress={() => leftSidebarStore.setShouldAutoscroll(true)}
          {...rest}
        />
      )
    },
  ),
)

interface GroupedBreadcrumbProps {
  breadcrumb: BreadcrumbType[]
  postAt?: string
}

const GroupedBreadcrumb = ({ breadcrumb, postAt }: GroupedBreadcrumbProps) => {
  return (
    <li className={cx(Styles.breadcrumb, Styles.grouped)}>
      <DropdownTrigger containFocus={false}>
        <Button variant="tertiary" size="md" aria-label="more" className={Styles.moreButton}>
          ...
        </Button>
        {() => (
          <ul className={Styles.groupedBreadcrumbs}>
            {breadcrumb.map((breadcrumb) => (
              <li key={breadcrumb.id}>
                <BreadcrumbLink breadcrumb={breadcrumb} withDefaultIcon postAt={postAt} />
              </li>
            ))}
          </ul>
        )}
      </DropdownTrigger>
    </li>
  )
}

interface SingleBreadcrumbProps {
  breadcrumb: BreadcrumbType
  postAt?: string
}

const SingleBreadcrumb = ({ breadcrumb, postAt }: SingleBreadcrumbProps) => {
  const { title } = breadcrumb

  return (
    <li className={Styles.breadcrumb}>
      <Tooltip label={title}>
        <BreadcrumbLink breadcrumb={breadcrumb} postAt={postAt} />
      </Tooltip>
    </li>
  )
}

interface BreadcrumbProps {
  breadcrumb: BreadcrumbType | BreadcrumbType[]
  postAt?: string
}

const Breadcrumb = ({ breadcrumb, postAt }: BreadcrumbProps) => {
  if (Array.isArray(breadcrumb)) {
    return <GroupedBreadcrumb breadcrumb={breadcrumb} postAt={postAt} />
  }

  return <SingleBreadcrumb breadcrumb={breadcrumb} postAt={postAt} />
}

export const AlmanacFileBreadcrumbs = ({
  file,
  ancestors,
  assetType,
  children,
  showAllFiles = false,
  weight = 'medium',
}: PropsWithChildren<
  Omit<AlmanacFileBreadcrumbsContext, 'type' | 'id'> & {
    file: Omit<AlmanacFileSummary, 'ancestors'>
    showAllFiles?: boolean
    weight?: Weight
  }
>) => {
  const collapsedCrumbs = ancestors.slice(1, -1)
  const breadcrumbs = new Set(
    [ancestors[0], ...(collapsedCrumbs.length > 0 ? [collapsedCrumbs] : []), ancestors[ancestors.length - 1]].filter(
      Boolean,
    ),
  )

  const isFileWithNoAncestry = assetType !== 'Project' && breadcrumbs.size === 0

  return (
    <ul className={cx(Styles.breadcrumbs, Styles[weight])}>
      {isFileWithNoAncestry ? (
        <CanView
          as={[UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
          inWorkspace={file.workspace.id}
        >
          <li className={Styles.breadcrumb}>
            <BaseBreadcrumb
              to={generatePath(SHARED_WITH_ME_DOCS_PATH, { workspaceId: file.workspace.id })}
              icon={<Share />}
              title="Shared With Me"
            />
          </li>
        </CanView>
      ) : (
        <>
          {showAllFiles && (
            <li className={Styles.breadcrumb}>
              <Tooltip label="All Files">
                <BaseBreadcrumb
                  to={generatePath(ALL_FILES_PATH, { workspaceId: file.workspace.id })}
                  title="All Files"
                  icon={<FolderOpen />}
                />
              </Tooltip>
            </li>
          )}
          {Array.from(breadcrumbs).map((breadcrumb, index) => (
            <Breadcrumb breadcrumb={breadcrumb} key={index} postAt={file.post_at} />
          ))}
        </>
      )}
      {children}
    </ul>
  )
}

interface AlmanacFileTitleProps extends Pick<ButtonProps, 'size'> {
  id: string
  assetType: AssetType
}

export const AlmanacFileTitle = observer(
  ({ id, assetType, children, size }: PropsWithChildren<AlmanacFileTitleProps>) => {
    const rootStore = useRootStore()
    const file = rootStore.files.get(id)
    const articleView = useArticleView()
    const workspace = useWorkspace(file?.workspace?.id || null)
    const inViewMode = canViewIn(articleView)
    const { currentView } = useProseInstance()
    const wordCount = currentView?.state.doc && parseInt(wordCountOfDoc(currentView.state.doc))
    const isArticle = assetType === 'Article'
    const ability = useAbility(AbilityContext)

    if (!file) {
      return null
    }

    const subject = {
      role: workspace?.role || UserRole.UNAUTHED,
      permissions: file.permissions,
      __caslSubjectType__: assetType === 'Project' ? SubjectType.FOLDER : SubjectType.ALMANAC_FILE,
    }

    const isReadOnly =
      (assetType === 'Project' && ability.cannot('updateMetadata', subject)) ||
      (assetType === 'Article' && (ability.cannot('rename', subject) || !inViewMode(ArticleView.Editing))) ||
      ability.cannot('rename', subject)

    return (
      <AlmanacFileTitleWithTree
        id={id}
        assetType={assetType}
        forceOpen={isArticle && file.title === 'Untitled' && wordCount === 0}
        autoFocus
        withChannels={isArticle}
        size={size}
        isDisabled={isReadOnly}
      >
        {children}
      </AlmanacFileTitleWithTree>
    )
  },
)

export const AlmanacFileBreadcrumbsContainer = observer(
  ({ id, ancestors, assetType }: Omit<AlmanacFileBreadcrumbsContext, 'type'>) => {
    const rootStore = useRootStore()
    const file = rootStore.files.get(id)

    useEffect(() => {
      rootStore.files.fetch(id)
    }, [id, rootStore])

    if (!file) {
      return null
    }

    return (
      <ErrorBoundary fallback={<AlmanacFileTitle id={id} assetType={assetType} />}>
        <AlmanacFileBreadcrumbs file={file} ancestors={ancestors} assetType={assetType}>
          <li className={Styles.title} aria-label={file.title}>
            <AlmanacFileTitle id={id} assetType={assetType} />
          </li>
        </AlmanacFileBreadcrumbs>
      </ErrorBoundary>
    )
  },
)
